import { Button, Input, type RHF, Textarea } from "@devhop/ui";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVerticalIcon, PlusIcon, TrashIcon } from "lucide-react";
import { nanoid } from "nanoid";
import type { SOPFormStepDTO } from "@/modules/sop/domain/contracts";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormTipTapEditor } from "@/shared/ui/FormTipTapEditor";

type PositionItem = { id: string; name: string };

interface SOPStepsListProps {
  steps: SOPFormStepDTO[];
  onChange: (steps: SOPFormStepDTO[]) => void;
  formMethods: RHF.UseFormReturn<{ steps: SOPFormStepDTO[] }>;
}

function SortableStepItem({
  step,
  index,
  onUpdate,
  onDelete,
  formMethods: _formMethods,
}: {
  step: SOPFormStepDTO;
  index: number;
  onUpdate: (step: SOPFormStepDTO) => void;
  onDelete: () => void;
  formMethods: RHF.UseFormReturn<{ steps: SOPFormStepDTO[] }>;
}) {
  // formMethods is used by FormInfiniteCombobox via form context
  void _formMethods;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex gap-3 rounded-lg border bg-card p-4 ${
        isDragging ? "shadow-lg" : ""
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="mt-2 cursor-grab active:cursor-grabbing"
      >
        <GripVerticalIcon className="h-5 w-5 text-muted-foreground" />
      </button>

      <div className="flex-1 space-y-3">
        <div className="flex items-start gap-2">
          <div className="mt-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 font-medium text-primary text-sm">
            {step.stepNumber}
          </div>
          <div className="flex-1">
            <Input
              placeholder="ຂັ້ນຕອນທີ່ຕ້ອງເຮັດ..."
              value={step.title}
              onChange={(e) => onUpdate({ ...step, title: e.target.value })}
              className="font-medium"
            />
          </div>
        </div>

        <div className="pl-8">
          <FormTipTapEditor
            name={`steps.${index}.description`}
            placeholder="ລາຍລະອຽດຂັ້ນຕອນ..."
            toolbarElements={["bold", "italic", "bulletList", "orderedList"]}
          />
        </div>

        <div className="pl-8">
          <FormInfiniteCombobox<PositionItem>
            name={`steps.${index}.responsibleId`}
            label="ຜູ້ຮັບຜິດຊອບ"
            queryKey={["positions-lookup"]}
            queryFn={(args) =>
              fetchLookupForInfinite(`${config.apiUrl}/positions/lookup`, args)
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/positions/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກຜູ້ຮັບຜິດຊອບ..."
            clearable
          />
        </div>

        <div className="pl-8">
          <Textarea
            placeholder="ຫມາຍເຫດ (ຖ້າມີ)..."
            value={step.notes || ""}
            onChange={(e) =>
              onUpdate({ ...step, notes: e.target.value || null })
            }
            rows={2}
            className="resize-none"
          />
        </div>
      </div>

      <button
        type="button"
        onClick={onDelete}
        className="mt-2 text-destructive hover:text-destructive/80"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

export function SOPStepsList({
  steps,
  onChange,
  formMethods,
}: SOPStepsListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = steps.findIndex((s) => s.id === active.id);
      const newIndex = steps.findIndex((s) => s.id === over.id);

      const newSteps = arrayMove(steps, oldIndex, newIndex).map(
        (step, index) => ({
          ...step,
          stepNumber: index + 1,
        }),
      );

      onChange(newSteps);
    }
  };

  const handleAddStep = () => {
    const newStep: SOPFormStepDTO = {
      id: nanoid(),
      stepNumber: steps.length + 1,
      title: "",
      description: null,
      responsibleId: null,
      notes: null,
    };
    onChange([...steps, newStep]);
  };

  const handleUpdateStep = (index: number, updatedStep: SOPFormStepDTO) => {
    const newSteps = [...steps];
    newSteps[index] = updatedStep;
    onChange(newSteps);
  };

  const handleDeleteStep = (index: number) => {
    const newSteps = steps
      .filter((_, i) => i !== index)
      .map((step, index) => ({
        ...step,
        stepNumber: index + 1,
      }));
    onChange(newSteps);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-sm">ຂັ້ນຕອນການເຮັດວຽກ</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddStep}
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          ເພີ່ມຂັ້ນຕອນ
        </Button>
      </div>

      {steps.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground text-sm">
          ຍັງບໍ່ມີຂັ້ນຕອນ ກົດປຸ່ມ "ເພີ່ມຂັ້ນຕອນ" ເພື່ອເລີ່ມຕົ້ນ
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={steps.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {steps.map((step, index) => (
                <SortableStepItem
                  key={step.id}
                  step={step}
                  index={index}
                  onUpdate={(updated) => handleUpdateStep(index, updated)}
                  onDelete={() => handleDeleteStep(index)}
                  formMethods={formMethods}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
